import React from 'react';
import { Form, Input, Select, DatePicker } from 'antd';
import { Vehicle } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatNumberWithDotThousandCommaDecimal } from '../../utils/numberFormatter';

const { Option } = Select;

interface FuelReceiptFormFieldsProps {
  vehicles: Vehicle[];
  formData: {
    liters: number;
    pricePerLiter: number;
    totalAmount: number;
    odometer: number;
  };
  onNumericChange: (field: 'liters' | 'pricePerLiter' | 'totalAmount' | 'odometer', value: string) => void;
  isMobile?: boolean;
}

const FuelReceiptFormFields: React.FC<FuelReceiptFormFieldsProps> = ({
  vehicles,
  formData,
  onNumericChange,
  isMobile = false
}) => {
  const { t } = useLanguage();

  return (
    <>
      <Form.Item
        label={t('vehicle')}
        name="vehicleId"
        rules={[{ required: true, message: t('please.select') + ' ' + t('vehicle') }]}
      >
        <Select 
          placeholder={t('please.select') + ' ' + t('vehicle')}
          showSearch
          optionFilterProp="children"
        >
          {vehicles.map(vehicle => (
            <Option key={vehicle.id} value={vehicle.id}>
              {vehicle.licensePlate} - {vehicle.brand} {vehicle.model}
            </Option>
          ))}
        </Select>
      </Form.Item>
      
      <Form.Item
        label={t('refuel.date') || 'Refuel Date'}
        name="refuelDate"
        rules={[{ required: true, message: t('please.select') + ' ' + (t('refuel.date') || 'refuel date') }]}
      >
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item
        label={t('liters')}
        name="liters"
        rules={[{ required: true, message: t('please.input') + ' ' + t('liters') }]}
      >
        <Input
          type="text"
          value={formatNumberWithDotThousandCommaDecimal(formData.liters)}
          onChange={(e) => onNumericChange('liters', e.target.value)}
          addonAfter="L"
          placeholder={t('please.input') + ' ' + t('liters')}
        />
      </Form.Item>

      <Form.Item
        label={t('price.per.liter')}
        name="pricePerLiter"
        rules={[{ required: true, message: t('please.input') + ' ' + t('price.per.liter') }]}
      >
        <Input
          type="text"
          value={formatNumberWithDotThousandCommaDecimal(formData.pricePerLiter)}
          onChange={(e) => onNumericChange('pricePerLiter', e.target.value)}
          addonBefore="₫"
          placeholder={t('please.input') + ' ' + t('price.per.liter')}
        />
      </Form.Item>

      <Form.Item
        label={t('total.amount')}
        name="totalAmount"
        rules={[{ required: true, message: t('please.input') + ' ' + t('total.amount') }]}
      >
        <Input
          type="text"
          value={formatNumberWithDotThousandCommaDecimal(formData.totalAmount)}
          onChange={(e) => onNumericChange('totalAmount', e.target.value)}
          addonBefore="₫"
          placeholder={t('please.input') + ' ' + t('total.amount')}
        />
      </Form.Item>

      <Form.Item
        label={t('receipt.number') || 'Receipt Number'}
        name="receiptNumber"
      >
        <Input maxLength={100} placeholder={t('receipt.number') || 'Receipt Number'} />
      </Form.Item>

      <Form.Item
        label={t('gas.station')}
        name="gasStation"
      >
        <Input maxLength={200} />
      </Form.Item>

      <Form.Item
        label={t('odometer.reading') || t('odometer')}
        name="odometer"
      >
        <Input
          type="text"
          value={formatNumberWithDotThousandCommaDecimal(formData.odometer)}
          onChange={(e) => onNumericChange('odometer', e.target.value)}
          addonAfter="km"
          placeholder={t('odometer.reading') || t('odometer')}
        />
      </Form.Item>

      <Form.Item
        label={t('location')}
        name="location"
      >
        <Input maxLength={500} />
      </Form.Item>

      <Form.Item
        label={t('notes')}
        name="notes"
      >
        <Input.TextArea rows={3} />
      </Form.Item>
    </>
  );
};

export default FuelReceiptFormFields;
